'use client';
import { useEffect, useState } from 'react';
import SplitFlap from './SplitFlap';
import { FlipDot } from './FlipDot';

/* ============================================================
   BoardStates — the board when there is no board (§5.3).

   Four states, all rendered in the same material as the real board so an
   empty screen still looks like a departures board that happens to have
   nothing departing — never like an error page bolted onto a TV.

     <BoardMessage>    the shared shell: a flip-dot line + split-flap lines
     <NoLeagueState>   nothing configured yet — points at setup
     <InvalidLeague>   league_id fetched but Sleeper rejected it
     <OffseasonState>  valid league, no games (season_type off/pre)
     <LoadingState>    first fetch in flight — the board "warming up"

   The loading state deliberately riffles: a Solari board with nothing to
   say still clatters as it initializes, so the wait reads as hardware
   settling rather than a spinner.
   ============================================================ */

const AMBER = '#ffb300';
const DIM = '#5c6068';

// Hoisted out of LoadingState: a literal defined inside the component is a
// new array every render, which is what forced the exhaustive-deps suppression.
// At module scope the interval effect genuinely has no dependencies.
const LOADING_FRAMES = ['....', 'LOADING', 'CONNECTING', 'LOADING'];

export function BoardMessage({
  status = 'final',
  statusText = '',
  lines = [],
  size = 30,
  width = 22,
  hint = null,
  children = null,
}) {
  return (
    <div
      className="board-state"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        padding: '8vh 4vw',
        textAlign: 'center',
      }}
    >
      {statusText && (
        <FlipDot
          text={statusText.padEnd(8, ' ')}
          theme={status}
          dotSize={4}
          gap={1}
          speed={1}
          reveal="scatter"
          aria-label={statusText}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {lines.map((line, i) => (
          <SplitFlap
            key={i}
            text={line}
            size={size}
            width={width}
            align="center"
            color={i === 0 ? AMBER : undefined}
          />
        ))}
      </div>
      {hint && (
        <p style={{ color: DIM, fontSize: 13, letterSpacing: '.12em', margin: 0, maxWidth: 480 }}>
          {hint}
        </p>
      )}
      {children}
    </div>
  );
}

export function NoLeagueState({ size = 30 }) {
  return (
    <BoardMessage
      status="yellow"
      statusText="NO LEAGUE"
      lines={['NO LEAGUE', 'CONNECTED']}
      size={size}
      hint="Add a Sleeper league ID to start the board."
    />
  );
}

export function InvalidLeagueState({ leagueId, onReset, size = 30 }) {
  return (
    <BoardMessage
      status="yellow"
      statusText="NO DATA"
      lines={['LEAGUE NOT', 'FOUND']}
      size={size}
      hint={
        leagueId
          ? `Sleeper has no league with ID ${leagueId}. Check the ID and try again.`
          : 'Sleeper did not return a league for that ID.'
      }
    >
      {onReset && (
        <button type="button" onClick={onReset} style={buttonStyle}>
          CHANGE LEAGUE
        </button>
      )}
    </BoardMessage>
  );
}

export function OffseasonState({ leagueName = '', size = 30 }) {
  // Off-season is not an error — the board should feel like it's resting,
  // which is exactly the stillness the whole project is about.
  return (
    <BoardMessage
      status="white"
      statusText="OFFSEASN"
      lines={[leagueName || 'FANTASY', 'SEASON OVER']}
      size={size}
      hint="The board will wake up when the next season's first week opens."
    />
  );
}

export function LoadingState({ size = 30 }) {
  // riffle a few placeholder strings so the cells clatter while we wait
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % LOADING_FRAMES.length), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <BoardMessage
      status="yellow"
      statusText="STANDBY"
      lines={[LOADING_FRAMES[i]]}
      size={size}
    />
  );
}

const buttonStyle = {
  background: 'transparent',
  border: `1px solid ${DIM}`,
  color: AMBER,
  padding: '10px 18px',
  fontSize: 12,
  letterSpacing: '.2em',
  fontWeight: 700,
  cursor: 'pointer',
  borderRadius: 2,
};

export default BoardMessage;
